import type { GlossaryEntry, LocalizedTerm, SupportedLocale } from './types'

export const GLOSSARY = {
  // ---------------------------------------------------------------------------
  // Core creative units
  // ---------------------------------------------------------------------------
  shot: {
    en: 'Shot',
    definition: 'A single generated video on the canvas. The primary creative unit.',
    partOfSpeech: 'noun',
    usageRules: 'Use "Shot", never "Clip" or "Card".',
  },
  scene: {
    en: 'Scene',
    definition: 'A logical grouping of shots that belong together narratively.',
    partOfSpeech: 'noun',
  },
  take: {
    en: 'Take',
    definition: 'A variation of a shot generated with different parameters.',
    partOfSpeech: 'noun',
  },
  sequence: {
    en: 'Sequence',
    definition: 'An ordered series of shots assembled for playback or export.',
    partOfSpeech: 'noun',
  },

  // ---------------------------------------------------------------------------
  // Canvas objects
  // ---------------------------------------------------------------------------
  canvas: {
    en: 'Canvas',
    definition: 'The infinite 2D workspace where all nodes live.',
    partOfSpeech: 'noun',
  },
  node: {
    en: 'Node',
    definition: 'Any object placed on the canvas, such as a shot, image, bin, variable, annotation, or stack.',
    partOfSpeech: 'noun',
    usageRules: 'Use "node" in technical/developer contexts. In user-facing copy, use the specific kind name.',
  },
  bin: {
    en: 'Bin',
    definition: 'A spatial container on the canvas that groups related nodes.',
    partOfSpeech: 'noun',
    usageRules: 'Use "Bin", never "Folder" in canvas context.',
  },
  variable: {
    en: 'Variable',
    definition: 'A reusable prompt fragment placed on the canvas and referenced by shots.',
    partOfSpeech: 'noun',
  },
  annotation: {
    en: 'Text Annotation',
    definition: 'A text note placed on the canvas for organizational purposes.',
    partOfSpeech: 'noun',
  },
  stack: {
    en: 'Stack',
    definition:
      'A group of related shots or images stacked for comparison. Hold Alt and press the left or right arrow key to cycle through the stack.',
    partOfSpeech: 'noun',
  },

  // ---------------------------------------------------------------------------
  // 3D / Set
  // ---------------------------------------------------------------------------
  set: {
    en: 'Set',
    definition: 'A 3D environment used as a backdrop for shot generation.',
    partOfSpeech: 'noun',
  },
  'set-viewer': {
    en: 'Set Viewer',
    definition: 'The inline 3D viewport for navigating inside a set.',
    partOfSpeech: 'noun',
  },

  // ---------------------------------------------------------------------------
  // Interactions & UI concepts
  // ---------------------------------------------------------------------------
  'placement-mode': {
    en: 'Placement Mode',
    definition: 'The state entered when a creation shortcut is pressed. Click to place the new node.',
    partOfSpeech: 'noun',
  },
  'color-label': {
    en: 'Color Label',
    definition: 'A colored tag applied with the 6-0 keys to organize shots visually.',
    partOfSpeech: 'noun',
  },
  rating: {
    en: 'Rating',
    definition: 'A 1-5 star score on a shot. Favorite and reject flags are separate review markers.',
    partOfSpeech: 'noun',
  },
  marquee: {
    en: 'Marquee',
    definition: 'A drag-to-select rectangle drawn on empty canvas.',
    partOfSpeech: 'noun',
  },
  viewport: {
    en: 'Viewport',
    definition: 'The visible portion of the canvas, defined by position and zoom level.',
    partOfSpeech: 'noun',
  },
  'context-menu': {
    en: 'Context Menu',
    definition: 'The right-click menu on a node offering actions specific to that node type.',
    partOfSpeech: 'noun',
  },
  inspector: {
    en: 'Inspector',
    definition: 'The side panel showing details of the selected node.',
    partOfSpeech: 'noun',
  },
  'inline-rename': {
    en: 'Inline Rename',
    definition: 'The in-place text editing mode for renaming nodes directly on the canvas.',
    partOfSpeech: 'noun',
  },

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------
  generate: {
    en: 'Generate',
    definition: 'To create content using AI.',
    partOfSpeech: 'verb',
    usageRules: 'Use for AI-produced output. Use "Create" for user-initiated non-AI objects.',
  },
  create: {
    en: 'Create',
    definition: 'To make a new user object (bin, variable, annotation).',
    partOfSpeech: 'verb',
    usageRules: 'Use for non-AI creation. Use "Generate" for AI output.',
  },
  export: {
    en: 'Export',
    definition: 'To produce a final output file (MP4, PNG, etc.).',
    partOfSpeech: 'verb',
  },
  offshoot: {
    en: 'Offshoot',
    definition: "A derivative shot created by modifying an existing shot's prompt or settings.",
    partOfSpeech: 'noun',
  },
  extension: {
    en: 'Extension',
    definition: 'A shot that continues from the last frame of an existing shot.',
    partOfSpeech: 'noun',
  },

  // ---------------------------------------------------------------------------
  // Billing
  // ---------------------------------------------------------------------------
  olives: {
    en: 'Olives',
    definition: 'The spend unit in Martini.',
    partOfSpeech: 'noun',
    usageRules: 'Always "olives", never "credits" or "tokens".',
  },

  // ---------------------------------------------------------------------------
  // Avoided terms — included so linting can flag misuse
  // ---------------------------------------------------------------------------
  clip: {
    en: 'Clip',
    definition: 'Banned synonym for Shot.',
    partOfSpeech: 'noun',
    usageRules: 'AVOID. Use "Shot" instead.',
  },
  card: {
    en: 'Card',
    definition: 'Banned synonym for Shot or Node.',
    partOfSpeech: 'noun',
    usageRules: 'AVOID. Use "Shot" or the specific node kind.',
  },
  item: {
    en: 'Item',
    definition: 'Banned synonym for Node.',
    partOfSpeech: 'noun',
    usageRules: 'AVOID. Use "Node" or the specific kind name.',
  },
} as const satisfies Record<string, GlossaryEntry>

export type GlossaryKey = keyof typeof GLOSSARY

export const AVOIDED_GLOSSARY_KEYS: readonly GlossaryKey[] = ['clip', 'card', 'item']

export interface LocalizedGlossaryEntry {
  term: string
  definition: string
  partOfSpeech?: string
  usageRules?: string
}

const GLOSSARY_TEXT_JA = {
  shot: {
    term: 'ショット',
    definition: 'キャンバス上の1つの生成動画。主要な制作単位です。',
    partOfSpeech: '名詞',
    usageRules: '「Clip」や「Card」ではなく、「Shot」を使います。',
  },
  scene: {
    term: 'シーン',
    definition: '物語上ひとまとまりになるショットの論理的なグループです。',
    partOfSpeech: '名詞',
  },
  take: {
    term: 'テイク',
    definition: '異なるパラメータで生成したショットのバリエーションです。',
    partOfSpeech: '名詞',
  },
  sequence: {
    term: 'シーケンス',
    definition: '再生または書き出しのために並べたショットの連なりです。',
    partOfSpeech: '名詞',
  },
  canvas: {
    term: 'キャンバス',
    definition: 'すべてのノードが存在する無限の2Dワークスペースです。',
    partOfSpeech: '名詞',
  },
  node: {
    term: 'ノード',
    definition: 'ショット、画像、ビン、変数、注釈、スタックなど、キャンバス上に配置される任意のオブジェクトです。',
    partOfSpeech: '名詞',
    usageRules: '技術者向けの文脈では「node」を使います。ユーザー向けコピーでは、具体的な種類名を使います。',
  },
  bin: {
    term: 'ビン',
    definition: '関連するノードをまとめる、キャンバス上の空間的なコンテナです。',
    partOfSpeech: '名詞',
    usageRules: 'キャンバスの文脈では「Folder」ではなく「Bin」を使います。',
  },
  variable: {
    term: '変数',
    definition: 'キャンバスに置いてショットから参照できる、再利用可能なプロンプト断片です。',
    partOfSpeech: '名詞',
  },
  annotation: {
    term: 'テキスト注釈',
    definition: '整理のためにキャンバス上に置くテキストメモです。',
    partOfSpeech: '名詞',
  },
  stack: {
    term: 'スタック',
    definition: '比較のために重ねた関連ショットや画像のグループです。メンバーは矢印キーで切り替えます。',
    partOfSpeech: '名詞',
  },
  set: {
    term: 'セット',
    definition: 'ショット生成の背景として使う3D環境です。',
    partOfSpeech: '名詞',
  },
  'set-viewer': {
    term: 'セットビューアー',
    definition: 'セット内を移動するためのインライン3Dビューポートです。',
    partOfSpeech: '名詞',
  },
  'placement-mode': {
    term: '配置モード',
    definition: '作成ショートカットを押したときに入る状態です。クリックすると新しいノードを配置できます。',
    partOfSpeech: '名詞',
  },
  'color-label': {
    term: 'カラーレーベル',
    definition: '視覚的な整理のためにショットへ適用する色付きタグ（1〜5）です。',
    partOfSpeech: '名詞',
  },
  rating: {
    term: 'レーティング',
    definition: 'ショットに付けるお気に入りまたはリジェクトのフラグです。',
    partOfSpeech: '名詞',
  },
  marquee: {
    term: 'マーキー',
    definition: '空のキャンバス上をドラッグして描く選択矩形です。',
    partOfSpeech: '名詞',
  },
  viewport: {
    term: 'ビューポート',
    definition: '位置とズームレベルで定義される、キャンバスの表示範囲です。',
    partOfSpeech: '名詞',
  },
  'context-menu': {
    term: 'コンテキストメニュー',
    definition: 'ノード上で右クリックしたときに表示される、そのノードタイプ固有の操作メニューです。',
    partOfSpeech: '名詞',
  },
  inspector: {
    term: 'インスペクター',
    definition: '選択したノードの詳細を表示するサイドパネルです。',
    partOfSpeech: '名詞',
  },
  'inline-rename': {
    term: 'インライン名前変更',
    definition: 'キャンバス上でノード名を直接編集するモードです。',
    partOfSpeech: '名詞',
  },
  generate: {
    term: '生成',
    definition: 'AIを使ってコンテンツを作成することです。',
    partOfSpeech: '動詞',
    usageRules: 'AIが出力するものに使います。ユーザーが作る非AIオブジェクトには「Create」を使います。',
  },
  create: {
    term: '作成',
    definition: 'ビン、変数、注釈などの新しいユーザーオブジェクトを作ることです。',
    partOfSpeech: '動詞',
    usageRules: '非AIの作成に使います。AI出力には「Generate」を使います。',
  },
  export: {
    term: '書き出し',
    definition: 'MP4やPNGなどの最終出力ファイルを作成することです。',
    partOfSpeech: '動詞',
  },
  offshoot: {
    term: '派生ショット',
    definition: '既存ショットのプロンプトや設定を変更して作成した派生ショットです。',
    partOfSpeech: '名詞',
  },
  extension: {
    term: '延長',
    definition: '既存ショットの最後のフレームから続くショットです。',
    partOfSpeech: '名詞',
  },
  olives: {
    term: 'Olive',
    definition: 'Martiniでの使用単位です。',
    partOfSpeech: '名詞',
    usageRules: '常に「olives」を使い、「credits」や「tokens」は使いません。',
  },
  clip: {
    term: 'クリップ',
    definition: 'Shotの使用禁止の同義語です。',
    partOfSpeech: '名詞',
    usageRules: '使用しないでください。代わりに「Shot」を使います。',
  },
  card: {
    term: 'カード',
    definition: 'ShotまたはNodeの使用禁止の同義語です。',
    partOfSpeech: '名詞',
    usageRules: '使用しないでください。「Shot」または具体的なノード種別を使います。',
  },
  item: {
    term: 'アイテム',
    definition: 'Nodeの使用禁止の同義語です。',
    partOfSpeech: '名詞',
    usageRules: '使用しないでください。「Node」または具体的な種類名を使います。',
  },
} satisfies Record<GlossaryKey, LocalizedTerm>

const GLOSSARY_TEXT_ES = {
  shot: {
    term: 'Toma',
    definition: 'Un video generado individual en el lienzo. La unidad creativa principal.',
    partOfSpeech: 'sustantivo',
    usageRules: 'Usa "Toma" (o "Shot"), nunca "Clip" ni "Card".',
  },
  scene: {
    term: 'Escena',
    definition: 'Una agrupación lógica de tomas que pertenecen juntas narrativamente.',
    partOfSpeech: 'sustantivo',
  },
  take: {
    term: 'Take',
    definition: 'Una variación de una toma generada con parámetros distintos.',
    partOfSpeech: 'sustantivo',
  },
  sequence: {
    term: 'Secuencia',
    definition: 'Una serie ordenada de tomas ensambladas para reproducción o exportación.',
    partOfSpeech: 'sustantivo',
  },
  canvas: {
    term: 'Lienzo',
    definition: 'El espacio de trabajo 2D infinito donde viven todos los nodos.',
    partOfSpeech: 'sustantivo',
  },
  node: {
    term: 'Nodo',
    definition: 'Cualquier objeto colocado en el lienzo, como una toma, imagen, bin, variable, anotación o pila.',
    partOfSpeech: 'sustantivo',
    usageRules: 'Usa "nodo" en contextos técnicos. En copy de usuario, usa el nombre específico del tipo.',
  },
  bin: {
    term: 'Bin',
    definition: 'Un contenedor espacial en el lienzo que agrupa nodos relacionados.',
    partOfSpeech: 'sustantivo',
    usageRules: 'Usa "Bin", nunca "Carpeta" en el contexto del lienzo.',
  },
  variable: {
    term: 'Variable',
    definition: 'Un fragmento de prompt reutilizable colocado en el lienzo y referenciado desde las tomas.',
    partOfSpeech: 'sustantivo',
  },
  annotation: {
    term: 'Anotación de texto',
    definition: 'Una nota de texto colocada en el lienzo para organizar.',
    partOfSpeech: 'sustantivo',
  },
  stack: {
    term: 'Pila',
    definition:
      'Un grupo de tomas o imágenes relacionadas apiladas juntas para comparar. Recorre los miembros con las teclas de flecha.',
    partOfSpeech: 'sustantivo',
  },
  set: {
    term: 'Set',
    definition: 'Un entorno 3D usado como fondo para generar tomas.',
    partOfSpeech: 'sustantivo',
  },
  'set-viewer': {
    term: 'Visor de sets',
    definition: 'Un viewport 3D en línea para moverte dentro de un set.',
    partOfSpeech: 'sustantivo',
  },
  'placement-mode': {
    term: 'Modo de colocación',
    definition: 'El estado en el que entras tras pulsar un atajo de creación. Haz clic para colocar el nuevo nodo.',
    partOfSpeech: 'sustantivo',
  },
  'color-label': {
    term: 'Etiqueta de color',
    definition: 'Una etiqueta de color (1-5) aplicada a las tomas para organización visual.',
    partOfSpeech: 'sustantivo',
  },
  rating: {
    term: 'Calificación',
    definition: 'Una marca de favorito o descarte aplicada a una toma.',
    partOfSpeech: 'sustantivo',
  },
  marquee: {
    term: 'Marco de selección',
    definition: 'El rectángulo de selección que dibujas arrastrando sobre un área vacía del lienzo.',
    partOfSpeech: 'sustantivo',
  },
  viewport: {
    term: 'Viewport',
    definition: 'La porción visible del lienzo, definida por la posición y el nivel de zoom.',
    partOfSpeech: 'sustantivo',
  },
  'context-menu': {
    term: 'Menú contextual',
    definition:
      'El menú que aparece al hacer clic derecho sobre un nodo, con acciones específicas para ese tipo de nodo.',
    partOfSpeech: 'sustantivo',
  },
  inspector: {
    term: 'Inspector',
    definition: 'El panel lateral que muestra los detalles del nodo seleccionado.',
    partOfSpeech: 'sustantivo',
  },
  'inline-rename': {
    term: 'Renombrado en línea',
    definition: 'El modo para editar el nombre de un nodo directamente en el lienzo.',
    partOfSpeech: 'sustantivo',
  },
  generate: {
    term: 'Generar',
    definition: 'Crear contenido usando IA.',
    partOfSpeech: 'verbo',
    usageRules: 'Úsalo para lo que produce la IA. Usa "Crear" para los objetos no-IA que hace el usuario.',
  },
  create: {
    term: 'Crear',
    definition: 'Crear un nuevo objeto del usuario como un bin, una variable o una anotación.',
    partOfSpeech: 'verbo',
    usageRules: 'Úsalo para creación no-IA. Usa "Generar" para los resultados de la IA.',
  },
  export: {
    term: 'Exportar',
    definition: 'Crear un archivo de salida final como un MP4 o un PNG.',
    partOfSpeech: 'verbo',
  },
  offshoot: {
    term: 'Toma derivada',
    definition: 'Una toma derivada creada a partir de una existente cambiando su prompt o sus ajustes.',
    partOfSpeech: 'sustantivo',
  },
  extension: {
    term: 'Extensión',
    definition: 'Una toma que continúa desde el último frame de una toma existente.',
    partOfSpeech: 'sustantivo',
  },
  olives: {
    term: 'Olive',
    definition: 'La unidad de uso en Martini.',
    partOfSpeech: 'sustantivo',
    usageRules: 'Usa siempre "olives", nunca "créditos" ni "tokens".',
  },
  clip: {
    term: 'Clip',
    definition: 'Sinónimo no permitido de Shot.',
    partOfSpeech: 'sustantivo',
    usageRules: 'No usar. Usa "Toma" (o "Shot") en su lugar.',
  },
  card: {
    term: 'Card',
    definition: 'Sinónimo no permitido de Shot o Node.',
    partOfSpeech: 'sustantivo',
    usageRules: 'No usar. Usa "Toma" o el tipo de nodo específico.',
  },
  item: {
    term: 'Item',
    definition: 'Sinónimo no permitido de Node.',
    partOfSpeech: 'sustantivo',
    usageRules: 'No usar. Usa "Nodo" o el nombre del tipo específico.',
  },
} satisfies Record<GlossaryKey, LocalizedTerm>

const GLOSSARY_TEXT_AR = {
  shot: {
    term: 'لقطة',
    definition: 'فيديو واحد مولّد على الكانفاس. الوحدة الإبداعية الأساسية.',
    partOfSpeech: 'اسم',
    usageRules: 'استخدم "لقطة"، وليس "Clip" أو "Card".',
  },
  scene: {
    term: 'مشهد',
    definition: 'مجموعة منطقية من اللقطات التي تنتمي سرديًا إلى بعضها.',
    partOfSpeech: 'اسم',
  },
  take: {
    term: 'نسخة',
    definition: 'تنويعة من لقطة مولّدة باستخدام معلمات مختلفة.',
    partOfSpeech: 'اسم',
  },
  sequence: {
    term: 'تسلسل',
    definition: 'سلسلة مرتبة من اللقطات مجمّعة للتشغيل أو التصدير.',
    partOfSpeech: 'اسم',
  },
  canvas: {
    term: 'كانفاس',
    definition: 'مساحة العمل ثنائية الأبعاد اللامحدودة التي تعيش فيها كل العقد.',
    partOfSpeech: 'اسم',
  },
  node: {
    term: 'عقدة',
    definition: 'أي كائن موضوع على الكانفاس، مثل لقطة أو صورة أو حاوية أو متغير أو تعليق أو مكدس.',
    partOfSpeech: 'اسم',
    usageRules: 'استخدم "عقدة" في السياقات التقنية. في النصوص الموجهة للمستخدم، استخدم اسم النوع المحدد.',
  },
  bin: {
    term: 'حاوية',
    definition: 'حاوية مكانية على الكانفاس تجمع العقد ذات الصلة.',
    partOfSpeech: 'اسم',
    usageRules: 'استخدم "حاوية" أو "Bin"، وليس "Folder" في سياق الكانفاس.',
  },
  variable: {
    term: 'متغير',
    definition: 'جزء قابل لإعادة الاستخدام من مطالبة، يوضع على الكانفاس وتستدعيه اللقطات.',
    partOfSpeech: 'اسم',
  },
  annotation: {
    term: 'تعليق نصي',
    definition: 'ملاحظة نصية توضع على الكانفاس لأغراض التنظيم.',
    partOfSpeech: 'اسم',
  },
  stack: {
    term: 'مكدس',
    definition:
      'مجموعة من اللقطات أو الصور ذات الصلة موضوعة فوق بعضها للمقارنة. يتم التنقل بين أعضائها بمفاتيح الأسهم.',
    partOfSpeech: 'اسم',
  },
  set: {
    term: 'مجموعة',
    definition: 'بيئة ثلاثية الأبعاد تُستخدم كخلفية لإنشاء اللقطات.',
    partOfSpeech: 'اسم',
  },
  'set-viewer': {
    term: 'عارض المجموعة',
    definition: 'نافذة عرض ثلاثية الأبعاد مدمجة للتنقل داخل المجموعة.',
    partOfSpeech: 'اسم',
  },
  'placement-mode': {
    term: 'وضع placement',
    definition: 'الحالة التي تدخلها عند الضغط على اختصار إنشاء. انقر لوضع العقدة الجديدة.',
    partOfSpeech: 'اسم',
  },
  'color-label': {
    term: 'تسمية لون',
    definition: 'وسم ملوّن (1-5) يطبّق على اللقطات للتنظيم البصري.',
    partOfSpeech: 'اسم',
  },
  rating: {
    term: 'تقييم',
    definition: 'علامة مفضلة أو رفض على لقطة.',
    partOfSpeech: 'اسم',
  },
  marquee: {
    term: 'تحديد بسحب مستطيل',
    definition: 'مستطيل تحديد ترسمه بالسحب على مساحة فارغة من الكانفاس.',
    partOfSpeech: 'اسم',
  },
  viewport: {
    term: 'نافذة العرض',
    definition: 'الجزء المرئي من الكانفاس، ويحدده الموضع ومستوى التكبير.',
    partOfSpeech: 'اسم',
  },
  'context-menu': {
    term: 'قائمة السياق',
    definition: 'قائمة النقر بزر الفأرة الأيمن على عقدة، وتعرض إجراءات خاصة بنوع تلك العقدة.',
    partOfSpeech: 'اسم',
  },
  inspector: {
    term: 'المفتش',
    definition: 'اللوحة الجانبية التي تعرض تفاصيل العقدة المحددة.',
    partOfSpeech: 'اسم',
  },
  'inline-rename': {
    term: 'إعادة تسمية مباشرة',
    definition: 'وضع تحرير النص في مكانه لإعادة تسمية العقد مباشرة على الكانفاس.',
    partOfSpeech: 'اسم',
  },
  generate: {
    term: 'توليد',
    definition: 'إنشاء محتوى باستخدام الذكاء الاصطناعي.',
    partOfSpeech: 'فعل',
    usageRules:
      'يُستخدم للمخرجات التي ينتجها الذكاء الاصطناعي. استخدم "إنشاء" للكائنات غير الذكية التي يبدأها المستخدم.',
  },
  create: {
    term: 'إنشاء',
    definition: 'صنع كائن مستخدم جديد، مثل حاوية أو متغير أو تعليق.',
    partOfSpeech: 'فعل',
    usageRules: 'يُستخدم للإنشاء غير المعتمد على الذكاء الاصطناعي. استخدم "توليد" لمخرجات الذكاء الاصطناعي.',
  },
  export: {
    term: 'تصدير',
    definition: 'إنتاج ملف إخراج نهائي، مثل MP4 أو PNG.',
    partOfSpeech: 'فعل',
  },
  offshoot: {
    term: 'لقطة مشتقة',
    definition: 'لقطة مشتقة تُنشأ بتعديل مطالبة أو إعدادات لقطة موجودة.',
    partOfSpeech: 'اسم',
  },
  extension: {
    term: 'امتداد',
    definition: 'لقطة تتابع من آخر إطار في لقطة موجودة.',
    partOfSpeech: 'اسم',
  },
  olives: {
    term: 'Olive',
    definition: 'وحدة الإنفاق في Martini.',
    partOfSpeech: 'اسم',
    usageRules: 'استخدم دائمًا "olives"، وليس "credits" أو "tokens".',
  },
  clip: {
    term: 'Clip',
    definition: 'مرادف ممنوع لـ Shot.',
    partOfSpeech: 'اسم',
    usageRules: 'لا تستخدمه. استخدم "لقطة" بدلًا منه.',
  },
  card: {
    term: 'Card',
    definition: 'مرادف ممنوع لـ Shot أو Node.',
    partOfSpeech: 'اسم',
    usageRules: 'لا تستخدمه. استخدم "لقطة" أو نوع العقدة المحدد.',
  },
  item: {
    term: 'Item',
    definition: 'مرادف ممنوع لـ Node.',
    partOfSpeech: 'اسم',
    usageRules: 'لا تستخدمه. استخدم "عقدة" أو اسم النوع المحدد.',
  },
} satisfies Record<GlossaryKey, LocalizedTerm>

const GLOSSARY_TEXT_KO = {
  shot: {
    term: '샷',
    definition: '캔버스에 있는 하나의 생성 비디오입니다. 기본 제작 단위입니다.',
    partOfSpeech: '명사',
    usageRules: '"Clip"이나 "Card"가 아니라 "Shot" 또는 "샷"을 사용합니다.',
  },
  scene: {
    term: '씬',
    definition: '서사적으로 함께 속하는 샷의 논리적 묶음입니다.',
    partOfSpeech: '명사',
  },
  take: {
    term: '테이크',
    definition: '다른 파라미터로 생성한 샷의 변형입니다.',
    partOfSpeech: '명사',
  },
  sequence: {
    term: '시퀀스',
    definition: '재생 또는 내보내기를 위해 순서대로 조립한 샷의 묶음입니다.',
    partOfSpeech: '명사',
  },
  canvas: {
    term: '캔버스',
    definition: '모든 노드가 놓이는 무한 2D 작업 공간입니다.',
    partOfSpeech: '명사',
  },
  node: {
    term: '노드',
    definition: '샷, 이미지, 빈, 변수, 주석, 스택처럼 캔버스에 배치되는 모든 객체입니다.',
    partOfSpeech: '명사',
    usageRules: '기술/개발자 문맥에서는 "node"를 사용합니다. 사용자-facing 문구에서는 구체적인 종류 이름을 사용합니다.',
  },
  bin: {
    term: '빈',
    definition: '관련 노드를 묶는 캔버스의 공간 컨테이너입니다.',
    partOfSpeech: '명사',
    usageRules: '캔버스 문맥에서는 "Folder"가 아니라 "Bin"을 사용합니다.',
  },
  variable: {
    term: '변수',
    definition: '캔버스에 배치하고 샷에서 참조하는 재사용 가능한 프롬프트 조각입니다.',
    partOfSpeech: '명사',
  },
  annotation: {
    term: '텍스트 주석',
    definition: '정리를 위해 캔버스에 배치하는 텍스트 메모입니다.',
    partOfSpeech: '명사',
  },
  stack: {
    term: '스택',
    definition: '비교를 위해 쌓아 둔 관련 샷이나 이미지 그룹입니다. 멤버는 화살표 키로 넘깁니다.',
    partOfSpeech: '명사',
  },
  set: {
    term: '세트',
    definition: '샷 생성의 배경으로 사용하는 3D 환경입니다.',
    partOfSpeech: '명사',
  },
  'set-viewer': {
    term: '세트 뷰어',
    definition: '세트 안을 탐색하기 위한 인라인 3D 뷰포트입니다.',
    partOfSpeech: '명사',
  },
  'placement-mode': {
    term: '배치 모드',
    definition: '생성 단축키를 눌렀을 때 들어가는 상태입니다. 클릭하면 새 노드를 배치합니다.',
    partOfSpeech: '명사',
  },
  'color-label': {
    term: '색상 라벨',
    definition: '시각적 정리를 위해 샷에 적용하는 색상 태그(1-5)입니다.',
    partOfSpeech: '명사',
  },
  rating: {
    term: '평가',
    definition: '샷에 붙이는 즐겨찾기 또는 리젝트 플래그입니다.',
    partOfSpeech: '명사',
  },
  marquee: {
    term: '마키',
    definition: '빈 캔버스 영역을 드래그해 그리는 선택 사각형입니다.',
    partOfSpeech: '명사',
  },
  viewport: {
    term: '뷰포트',
    definition: '위치와 줌 레벨로 정의되는 캔버스의 보이는 영역입니다.',
    partOfSpeech: '명사',
  },
  'context-menu': {
    term: '컨텍스트 메뉴',
    definition: '노드를 오른쪽 클릭했을 때 나타나며 해당 노드 타입에 맞는 작업을 제공하는 메뉴입니다.',
    partOfSpeech: '명사',
  },
  inspector: {
    term: '인스펙터',
    definition: '선택한 노드의 세부 정보를 보여 주는 사이드 패널입니다.',
    partOfSpeech: '명사',
  },
  'inline-rename': {
    term: '인라인 이름 변경',
    definition: '캔버스에서 노드 이름을 직접 편집하는 텍스트 편집 모드입니다.',
    partOfSpeech: '명사',
  },
  generate: {
    term: '생성',
    definition: 'AI를 사용해 콘텐츠를 만드는 것입니다.',
    partOfSpeech: '동사',
    usageRules: 'AI가 만든 출력에 사용합니다. 사용자가 직접 만드는 비AI 객체에는 "Create"를 사용합니다.',
  },
  create: {
    term: '만들기',
    definition: '빈, 변수, 주석 같은 새 사용자 객체를 만드는 것입니다.',
    partOfSpeech: '동사',
    usageRules: '비AI 생성에 사용합니다. AI 출력에는 "Generate"를 사용합니다.',
  },
  export: {
    term: '내보내기',
    definition: 'MP4, PNG 같은 최종 출력 파일을 만드는 것입니다.',
    partOfSpeech: '동사',
  },
  offshoot: {
    term: '파생 샷',
    definition: '기존 샷의 프롬프트나 설정을 수정해 만든 파생 샷입니다.',
    partOfSpeech: '명사',
  },
  extension: {
    term: '연장',
    definition: '기존 샷의 마지막 프레임에서 이어지는 샷입니다.',
    partOfSpeech: '명사',
  },
  olives: {
    term: 'Olive',
    definition: 'Martini의 사용량 단위입니다.',
    partOfSpeech: '명사',
    usageRules: '항상 "olives"를 사용하고 "credits"나 "tokens"는 사용하지 않습니다.',
  },
  clip: {
    term: '클립',
    definition: 'Shot의 금지된 동의어입니다.',
    partOfSpeech: '명사',
    usageRules: '사용하지 마세요. 대신 "Shot" 또는 "샷"을 사용합니다.',
  },
  card: {
    term: '카드',
    definition: 'Shot 또는 Node의 금지된 동의어입니다.',
    partOfSpeech: '명사',
    usageRules: '사용하지 마세요. "Shot" 또는 구체적인 노드 종류를 사용합니다.',
  },
  item: {
    term: '아이템',
    definition: 'Node의 금지된 동의어입니다.',
    partOfSpeech: '명사',
    usageRules: '사용하지 마세요. "Node" 또는 구체적인 종류 이름을 사용합니다.',
  },
} satisfies Record<GlossaryKey, LocalizedTerm>

const GLOSSARY_TEXT_BY_LOCALE: Partial<Record<SupportedLocale, Record<GlossaryKey, LocalizedTerm>>> = {
  ja: GLOSSARY_TEXT_JA,
  ar: GLOSSARY_TEXT_AR,
  es: GLOSSARY_TEXT_ES,
  ko: GLOSSARY_TEXT_KO,
}

export function getLocalizedGlossaryEntry(key: GlossaryKey, locale: SupportedLocale | string): LocalizedGlossaryEntry {
  const entry: GlossaryEntry = GLOSSARY[key]
  const localizedMap = GLOSSARY_TEXT_BY_LOCALE[locale as SupportedLocale]
  if (localizedMap) {
    const localized: LocalizedTerm = localizedMap[key]
    return {
      term: localized.term,
      definition: localized.definition,
      partOfSpeech: localized.partOfSpeech ?? entry.partOfSpeech,
      usageRules: localized.usageRules ?? entry.usageRules,
    }
  }

  return {
    term: entry.en,
    definition: entry.definition,
    partOfSpeech: entry.partOfSpeech,
    usageRules: entry.usageRules,
  }
}

export { type GlossaryEntry, type SupportedLocale } from './types'
